import { ArrowRight, ArrowRightLeft, CalendarDays, MoveRight } from 'lucide-react';
import type { BookingReassignmentKind, ObjectApartmentSummary } from '@/shared/api';
import { unchangedStayLabel } from './reassignmentPresentation';

interface Props {
  sourceName: string;
  targetName: string;
  target: string;
  options: ObjectApartmentSummary[];
  unavailable?: ReadonlyMap<string, string>;
  checkInDate: string;
  checkOutDate: string;
  kind?: BookingReassignmentKind;
  checking: boolean;
  problem: string | null;
  reason: string;
  onTargetChange: (value: string) => void;
  onReasonChange: (value: string) => void;
}

export function BookingReassignmentDialogBody(props: Props) {
  const reasonOf = (propertyId: string) => props.unavailable?.get(propertyId);
  const refusals = props.options.flatMap((property) => reasonOf(property.id) ?? []);
  return (
    <>
      <div className="bg-card-alt/60 border-line grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-lg border p-4">
        <Apartment label="Aktuell" name={props.sourceName} />
        <ArrowRight className="text-sage" aria-hidden="true" size={20} />
        <Apartment label="Ziel" name={props.targetName} />
      </div>
      <label className="block text-sm font-semibold" htmlFor="reassignment-target">
        Ziel-Apartment
      </label>
      <select
        id="reassignment-target"
        value={props.target}
        onChange={(event) => props.onTargetChange(event.target.value)}
        className="border-line bg-card min-h-11 w-full rounded-md border px-3 text-sm"
      >
        <option value="">Bitte auswählen</option>
        {props.options.map((property) => (
          <option key={property.id} value={property.id} disabled={!!reasonOf(property.id)}>
            {property.adminDisplayName ?? property.name}
            {reasonOf(property.id) ? ' — durch Verbund belegt' : ''}
          </option>
        ))}
      </select>
      {refusals.length > 0 && (
        <ul className="text-ink-soft space-y-1 text-sm" aria-label="Nicht wählbare Ziele">
          {refusals.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
      <p className="text-ink-soft flex items-center gap-2 text-sm">
        <CalendarDays size={16} aria-hidden="true" />
        {unchangedStayLabel(props.checkInDate, props.checkOutDate)}
      </p>
      {props.checking && <p className="text-ink-soft text-sm">Prüfe Verfügbarkeit…</p>}
      {props.kind && (
        <div className="border-sage/40 bg-sage/8 rounded-lg border p-4" role="status">
          <p className="flex items-center gap-2 font-semibold">
            {props.kind === 'Swap' ? <ArrowRightLeft size={18} /> : <MoveRight size={18} />}
            {props.kind === 'Swap' ? 'Tausch möglich' : 'Verschieben möglich'}
          </p>
          <p className="text-ink-soft mt-1 text-sm">
            {props.kind === 'Swap'
              ? 'Die Gegenbuchung wechselt atomar in das bisherige Apartment.'
              : 'Das Ziel ist für den vollständigen Aufenthalt frei.'}
          </p>
        </div>
      )}
      {props.problem && (
        <p
          className="border-sealwax/30 bg-sealwax/5 text-sealwax rounded-lg border p-3 text-sm"
          role="alert"
        >
          {props.problem}
        </p>
      )}
      <label className="block text-sm font-semibold" htmlFor="reassignment-reason">
        Grund (optional)
      </label>
      <textarea
        id="reassignment-reason"
        value={props.reason}
        onChange={(event) => props.onReasonChange(event.target.value)}
        maxLength={500}
        rows={3}
        className="border-line bg-card w-full resize-y rounded-md border px-3 py-2 text-sm"
        placeholder="Zum Beispiel: Upgrade nach Absprache"
      />
    </>
  );
}

function Apartment({ label, name }: { label: string; name: string }) {
  return (
    <div className="min-w-0">
      <span className="text-ink-faint text-[10px] font-semibold uppercase tracking-[0.12em]">
        {label}
      </span>
      <p className="truncate text-sm font-semibold">{name}</p>
    </div>
  );
}
